// Tipos publicos da tela Analyst Notes (Equity > Gestao).
//
// Espelham os response models do backend `miscellaneous`
// (miscellaneous/app/core/models/models.py), servido em
// /miscellaneous/api/v1/analyst_notes.
//
// Barrel unico: o client (src/clients/compass/analystNotes.ts) e os
// componentes importam daqui.
//
// TRES COISAS QUE MOLDAM TODA A TELA:
//
// 1. A nota e de TICKERS **ou** de um SETOR, nunca dos dois (XOR validado no
//    servidor). Na nota de ticker, setor / subsetor / cobertura sao
//    resolvidos por ticker a cada leitura e chegam em `classifications`; na
//    nota de setor o campo `sector` vem no proprio documento. O front NUNCA
//    calcula nem envia `sector_key` - a normalizacao e do backend.
//
// 2. `created_at` / `updated_at` sao ISO em UTC SEM o sufixo "Z". Passar
//    direto em `new Date()` interpreta como hora local (-3h no Brasil).
//    Use `parseApiUtc` de ../utils/dates.
//
// 3. `summary` / `comment` sao HTML CRU nos dois sentidos - a API nao
//    sanitiza. Sanitize ao renderizar (../utils/sanitize).
//
// 4. O feed traz RESUMOS (AnalystNoteSummary), sem `summary` / `comment`. O
//    conteudo so existe na nota completa (AnalystNote), de GET /notes/{id}.

// -- Sentimento ----------------------------------------------------

// Os tres unicos valores aceitos, NESTA ordem (nao e alfabetica), alem do
// vazio ("") de nota sem sentimento - que nunca vira Neutro.
// `GET /options` devolve exatamente isto; a constante existe como fallback
// para o caso de a lista vir vazia - o segmented control nunca pode ficar
// sem opcao.
export const SENTIMENTS = ['Positivo', 'Neutro', 'Negativo'] as const

export type Sentiment = (typeof SENTIMENTS)[number]

export const isSentiment = (v: string): v is Sentiment =>
  (SENTIMENTS as readonly string[]).includes(v)

/** Valor do FILTRO "notas sem sentimento" (`?sentiment=Sem sentimento`).
 *  So filtro: nunca e gravado numa nota nem oferecido no editor, por isso
 *  fica fora de SENTIMENTS e de `GET /options`. */
export const NO_SENTIMENT = 'Sem sentimento'

/** A tela e em ingles; a API e em portugues. Exiba o rotulo, ENVIE a chave -
 *  os filtros de sentimento sao case-sensitive no servidor. */
export const SENTIMENT_LABELS: Record<string, string> = {
  Positivo: 'Positive',
  Neutro: 'Neutral',
  Negativo: 'Negative',
  [NO_SENTIMENT]: 'No sentiment',
}

export const sentimentLabel = (value: string): string =>
  SENTIMENT_LABELS[value] ?? value

// -- Nota ----------------------------------------------------------

/** Corpo de POST /notes e PUT /notes/{id}. */
export interface AnalystNotePayload {
  /** Estilo Bloomberg ("PETR BZ"). O servidor faz trim+upper. Vazio quando a
   *  nota e de setor. */
  tickers: string[]
  /** Setor da nota, COMO O ANALISTA ESCREVEU (o servidor guarda tambem uma
   *  forma normalizada, que o front nunca envia). Vazio quando a nota e de
   *  tickers: mandar os dois preenchidos - ou os dois vazios - e 422. */
  sector: string
  /** YYYY-MM-DD. */
  date: string
  /** Um de SENTIMENTS, ou "" quando o analista nao deu sentimento. Vazio
   *  fica vazio: NUNCA converter para Neutro. Mande sempre a chave (PUT e
   *  substituicao total). */
  sentiment: string
  /** Texto livre, nao pode ser vazio. */
  note_type: string
  /** Opcional na API (default ""), OBRIGATORIO no produto. */
  title: string
  /** HTML. Opcional. */
  summary: string
  /** HTML. Opcional na API, OBRIGATORIO no produto. */
  comment: string
  /** Chave de um contexto de `GET /options` - quem pode LER a nota. So o POST
   *  usa: e imutavel depois de publicada, entao o client nao manda no PUT. */
  context: string
  /** Nome canonico de `options.brokers`, ou "" quando a nota nao e de broker.
   *  Nunca texto livre (422). */
  broker: string
  /** Avaliacao que o analista deu a interacao com o broker - um de
   *  `options.ratings`. OBRIGATORIO com broker e PROIBIDO sem ele (422 nos
   *  dois). Presenca e `== null`, nunca truthiness. No PUT vai sempre junto
   *  com `broker`. */
  rating: number | null
}

/** Classificacao de UM ticker, resolvida na leitura. Todos os campos podem
 *  vir null (empresa sem classificacao) - renderize um travessao, nunca a
 *  string "null". */
export interface AnalystNoteClassification {
  jgp_sector: string | null
  jgp_subsector: string | null
  /** Chave crua, ex. "nivel_1". Compare/filtre por este. */
  coverage: string | null
  /** Forma de exibicao, ex. "Nivel 1". Mostre este. */
  coverage_label: string | null
}

/** Card do feed (GET /notes/feed). NAO tem `summary` nem `comment`, de
 *  proposito: conteudo que nao foi carregado nao pode ser confundido com nota
 *  vazia - um editor montado a partir disto salvaria a nota sem comentario.
 *  O compilador barra quem tentar; o conteudo vem de AnalystNote. */
export interface AnalystNoteSummary {
  /** ObjectId do Mongo, hex de 24 chars. */
  id: string
  tickers: string[]
  sector: string
  /** `sector` normalizado, calculado pelo servidor. O front nunca envia. */
  sector_key: string
  /** YYYY-MM-DD. */
  date: string
  sentiment: string
  note_type: string
  title: string
  /** Texto puro do `summary` (ou do `comment`, sem resumo), ate 400
   *  caracteres; "" quando os dois sao vazios. E o texto do card. */
  preview: string
  broker: string
  rating: number | null
  context: string
  /** SEMPRE uma entrada por item de `tickers` (valores podem ser null). */
  classifications: Record<string, AnalystNoteClassification>
  /** true depois de um POST /notes/{id}/notify bem-sucedido. Nunca volta a
   *  false - nem quando a nota e editada. */
  notified: boolean
  /** ISO UTC SEM "Z"; null enquanto a nota nao foi enviada. */
  notified_at: string | null
  created_by: string
  updated_by: string
  /** ISO UTC SEM "Z" - ver nota 2 no topo. */
  created_at: string
  updated_at: string
}

/** Nota completa, com o conteudo: GET /notes/{id} e a resposta de POST/PUT. */
export interface AnalystNote extends AnalystNotePayload, AnalystNoteSummary {}

/** Resumo de uma nota completa, para a lista do feed. Campo a campo, e nao
 *  espalhando a nota: o spread levaria o HTML inteiro para a lista. */
export const toNoteSummary = (n: AnalystNote): AnalystNoteSummary => ({
  id: n.id,
  tickers: n.tickers,
  sector: n.sector,
  sector_key: n.sector_key,
  date: n.date,
  sentiment: n.sentiment,
  note_type: n.note_type,
  title: n.title,
  preview: n.preview,
  broker: n.broker,
  rating: n.rating,
  context: n.context,
  classifications: n.classifications,
  notified: n.notified,
  notified_at: n.notified_at,
  created_by: n.created_by,
  updated_by: n.updated_by,
  created_at: n.created_at,
  updated_at: n.updated_at,
})

/** Uma pagina de GET /notes/feed. */
export interface AnalystNotesFeedPage {
  notes: AnalystNoteSummary[]
  /** Total que casa com o filtro - nao o tamanho da pagina. */
  count: number
  /** Opaco: volta como `cursor` para a pagina seguinte. null na ultima. */
  next_cursor: string | null
}

// -- Rascunho ------------------------------------------------------

/** Corpo de PUT /drafts/{uuid}. Nada e obrigatorio e nada e validado, exceto
 *  o formato da data: ou "" ou YYYY-MM-DD (qualquer outra coisa e 422). */
export interface AnalystNoteDraftPayload {
  /** Id da nota em edicao; null quando o rascunho e de uma nota nova. */
  note_id: string | null
  tickers: string[]
  /** Rascunho NAO aplica o XOR de ticker/setor - e scratch, pode ter os dois
   *  meio preenchidos. */
  sector: string
  date: string
  sentiment: string
  note_type: string
  title: string
  summary: string
  comment: string
  /** "" = ainda nao escolhido. Rascunho antigo nao traz os tres campos. */
  context: string
  broker: string
  rating: number | null
}

export interface AnalystNoteDraft extends AnalystNoteDraftPayload {
  /** O UUID gerado pelo cliente e usado na URL. NAO e um ObjectId. */
  id: string
  user_email: string
  created_at: string
  updated_at: string
}

// -- Opcoes do formulario ------------------------------------------

/** Autor de nota, para o filtro de analista.
 *
 *  O conjunto e `distinct("created_by")` das notas - todo mundo que escreveu
 *  aparece. `recipients` entra so como fonte de nome: quando o autor nao esta
 *  la, o servidor devolve o e-mail cru tambem em `name`. */
export interface AnalystNoteAnalyst {
  email: string
  name: string
}

/** Um contexto em que o usuario pode ESCREVER (e ler). */
export interface AnalystNoteContext {
  key: string
  label: string
  /** Publico = todo mundo le. Nao publico = so os `members`. */
  public: boolean
  /** E-mails em minusculas. Preenchido so em contexto nao publico. */
  members: string[]
}

export interface AnalystNoteOptions {
  /** Universo de empresas ativas. E exatamente o conjunto que create/update
   *  aceita: dirigir o picker por aqui torna o 422 de ticker inalcancavel. */
  tickers: string[]
  /** jgp_sector distintos das mesmas empresas. O filtro `sector` e
   *  CASE-SENSITIVE: mande a string exatamente como veio daqui. */
  sectors: string[]
  /** DESCOBERTO das notas existentes, nao um enum fixo - por isso o campo
   *  Tipo e um combobox creatable, nunca um select fechado. */
  note_types: string[]
  /** Sempre SENTIMENTS. Nao inclui o vazio nem NO_SENTIMENT: esta lista vira
   *  os segmentos do editor. */
  sentiments: string[]
  /** Quem REALMENTE escreveu nota (`distinct("created_by")`) - o filtro nunca
   *  oferece alguem que devolveria feed vazio. */
  analysts: AnalystNoteAnalyst[]
  /** POR USUARIO: os contextos que ELE pode escrever, publico primeiro. Vazio
   *  enquanto carrega - e ai nenhum contexto e assumido. */
  contexts: AnalystNoteContext[]
  /** Nomes canonicos, ordenados. Lista fechada. */
  brokers: string[]
  /** Escala do rating do broker (ex. [1,2,3,4,5]). */
  ratings: number[]
}

export const EMPTY_OPTIONS: AnalystNoteOptions = {
  tickers: [],
  sectors: [],
  note_types: [],
  sentiments: [...SENTIMENTS],
  analysts: [],
  contexts: [],
  brokers: [],
  ratings: [],
}

// -- Destinatarios -------------------------------------------------

/** Uma pessoa de `GET /recipients`. E o unico diretorio de nome real de
 *  pessoa no dominio de Equity: derivar nome do e-mail da resultado errado
 *  (frust@jgp.com.br e Felipe Rust, nao "Frust"). */
export interface AnalystNoteRecipient {
  name: string
  email: string
}

/** Resposta de POST /notes/{id}/notify. Um envio por destinatario, entao a
 *  falha e PARCIAL: `result` e true se `sent` nao estiver vazio. */
export interface AnalystNoteNotifyResult {
  result: boolean
  sent: string[]
  failed: string[]
}

// -- Filtros / ordenacao (estado da tela) --------------------------

/** Filtros de GET /notes/feed. Todos opcionais, combinados com AND no
 *  servidor. `sector`, `sentiment` e `note_type` sao CASE-SENSITIVE; `ticker`
 *  e `analyst` nao. */
export interface AnalystNotesQuery {
  ticker?: string | null
  /** Vai repetido (`?tickers=A&tickers=B`): casa QUALQUER um deles, e
   *  `ticker` entra no mesmo OU. */
  tickers?: string[] | null
  /** Casa a nota de setor (`sector` da nota) OU a de ticker cujo `jgp_sector`
   *  derivado bate - a uniao e feita no servidor. */
  sector?: string | null
  /** E-mail do autor (`created_by`). Case-insensitive no servidor. */
  analyst?: string | null
  /** Um de SENTIMENTS, ou NO_SENTIMENT para as notas sem sentimento. */
  sentiment?: string | null
  note_type?: string | null
  /** YYYY-MM-DD, limite inferior inclusivo. */
  start_date?: string | null
  /** YYYY-MM-DD, limite superior inclusivo. */
  end_date?: string | null
}

export const QUERY_KEYS = [
  'ticker',
  'sector',
  'analyst',
  'sentiment',
  'note_type',
  'start_date',
  'end_date',
] as const

export type AnalystNotesFilters = {
  [K in (typeof QUERY_KEYS)[number]]: string | null
}

export const EMPTY_FILTERS: AnalystNotesFilters = {
  ticker: null,
  sector: null,
  analyst: null,
  sentiment: null,
  note_type: null,
  start_date: null,
  end_date: null,
}

export const hasActiveFilters = (f: AnalystNotesFilters): boolean =>
  QUERY_KEYS.some((k) => !!f[k])

/** Agrupamento do feed. O modo Date pagina; setor e subsetor precisam dos
 *  grupos completos e carregam a lista leve inteira - entao trocar entre Date
 *  e um deles recarrega, e entre setor e subsetor e so um regroup. A escolha
 *  e persistida em localStorage. */
export type GroupMode = 'date' | 'sector' | 'subsector'

export const GROUP_MODES: { id: GroupMode; label: string }[] = [
  { id: 'date', label: 'Date' },
  { id: 'sector', label: 'JGP sector' },
  { id: 'subsector', label: 'Subsector' },
]

export const GROUP_MODE_STORAGE_KEY = 'analyst-notes:group-by'

/** Legenda da ordem, ao lado do controle. */
export const sortCaption = (mode: GroupMode): string =>
  mode === 'date' ? 'Newest first' : 'Newest first within each group'

/** Um bloco do feed. */
export interface NoteGroup {
  key: string
  label: string
  notes: AnalystNoteSummary[]
}

// -- Filtro de data (presets) --------------------------------------

export type DatePreset = 'any' | 'last7' | 'last30' | 'thisMonth' | 'custom'

export const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'any', label: 'Any date' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'custom', label: 'Custom' },
]

/** Bucket das notas cujos tickers nao tem classificacao alguma. O espaco no
 *  inicio da chave e proposital: mantem o bucket estavel e distinto de um
 *  setor real com o mesmo nome. A ordenacao forca ele por ultimo. */
export const UNCLASSIFIED = ' unclassified'
export const UNCLASSIFIED_LABEL = 'Unclassified'

/** Setores / subsetores / coberturas de uma nota, deduplicados e ordenados. */
export interface NoteFacets {
  sectors: string[]
  subsectors: string[]
  coverages: string[]
}
